import React, { useState } from 'react';
import { DollarSign, TrendingUp, TrendingDown, ChevronDown, ChevronUp, PieChart, AlertCircle, CheckCircle, Download } from 'lucide-react';

/**
 * BudgetSuggestionsDisplay - Shows AI budget recommendations for Budget Agent
 *
 * Similar to TransactionsModal for Bank Agent - self-contained component
 * that receives data and renders the full UI with all budget suggestions
 */
const BudgetSuggestionsDisplay = ({ suggestions: rawSuggestions, budgetName, fiscalYear, currency = 'USD', onConfirm, onClose }) => {
  console.log('📊 BudgetSuggestionsDisplay RENDER - rawSuggestions:', rawSuggestions);
  console.log('📊 BudgetSuggestionsDisplay RENDER - currency:', currency);

  // Normalize suggestions - handle both array and object formats
  const suggestions = Array.isArray(rawSuggestions)
    ? rawSuggestions
    : (rawSuggestions?.suggestions || Object.values(rawSuggestions || {}));

  const [expandedSuggestions, setExpandedSuggestions] = useState(new Set());
  const [editedBudgets, setEditedBudgets] = useState({});

  // Toggle suggestion expansion
  const toggleSuggestion = (categoryId) => {
    setExpandedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Handle budget edit
  const handleBudgetEdit = (categoryId, value) => {
    setEditedBudgets(prev => ({
      ...prev,
      [categoryId]: parseFloat(value) || 0
    }));
  };

  // Get budget value (edited or original)
  const getBudgetValue = (suggestion) => {
    if (editedBudgets[suggestion.category_id] !== undefined) {
      return editedBudgets[suggestion.category_id];
    }
    return suggestion.suggested_budget || 0;
  };

  // Calculate total budget
  const totalBudget = (suggestions || []).reduce((sum, s) => {
    return sum + getBudgetValue(s);
  }, 0);

  // Format currency - uses dynamic currency from props
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get confidence color
  const getConfidenceConfig = (confidence) => {
    const configs = {
      high: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-300',
        icon: CheckCircle,
      },
      medium: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-700 dark:text-yellow-300',
        icon: AlertCircle,
      },
      low: {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-700 dark:text-red-300',
        icon: AlertCircle,
      }
    };
    return configs[confidence] || configs.medium;
  };

  // Export budget suggestions to CSV
  const exportToCSV = () => {
    const rows = [];

    // Format number with commas
    const formatNumber = (num) => {
      if (!num && num !== 0) return '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    };

    // Header row
    rows.push([
      'Category',
      `Budget Amount (${currency})`,
      'Growth Rate (%)',
      'Confidence',
      'Reasoning'
    ]);

    // Data rows
    (suggestions || []).forEach(suggestion => {
      // Handle growth_rate format
      const growthRate = suggestion.growth_rate !== undefined
        ? (Math.abs(suggestion.growth_rate) < 1 ? suggestion.growth_rate * 100 : suggestion.growth_rate)
        : '';

      rows.push([
        suggestion.category_name || suggestion.category_id || '',
        formatNumber(getBudgetValue(suggestion)),
        growthRate !== '' ? growthRate.toFixed(1) : '',
        suggestion.confidence || '',
        (suggestion.reasoning || '').replace(/"/g, '""')
      ]);
    });

    // Add summary row
    rows.push([]);
    rows.push(['Total Budget', formatNumber(totalBudget), '', '', '']);
    rows.push(['Fiscal Year', fiscalYear || '', '', '', '']);
    rows.push(['Currency', currency, '', '', '']);
    if (Object.keys(editedBudgets).length > 0) {
      rows.push(['Modified Categories', Object.keys(editedBudgets).length, '', '', '']);
    }

    // Convert to CSV string
    const csvContent = rows.map(row =>
      row.map(cell => {
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
          return `"${cellStr}"`;
        }
        return cellStr;
      }).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Budget_Suggestions_${budgetName || 'Export'}_FY${fiscalYear || ''}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!suggestions || suggestions.length === 0) {
    return (
      <div className="p-8 text-center">
        <PieChart className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">No Suggestions Generated</h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          AI could not generate budget suggestions. Try re-running the categorization.
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-[98%] max-w-[1800px] max-h-[90vh] flex flex-col shadow-2xl overflow-hidden p-6">
        {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-green-600" />
          Budget Suggestions
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {budgetName && `${budgetName} - `}FY {fiscalYear || '2026'} ({suggestions.length} categories)
        </p>
      </div>

      {/* Total Budget Summary */}
      <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">Total Budget</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(totalBudget)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400">{suggestions.length} Categories</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Avg: {formatCurrency(totalBudget / suggestions.length)}
            </p>
          </div>
        </div>
      </div>

      {/* Suggestions List */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2">
        {suggestions.map((suggestion, idx) => {
          const isExpanded = expandedSuggestions.has(suggestion.category_id);
          const confidenceConfig = getConfidenceConfig(suggestion.confidence);
          const ConfidenceIcon = confidenceConfig.icon;
          const budgetValue = getBudgetValue(suggestion);
          const isEdited = editedBudgets[suggestion.category_id] !== undefined;

          return (
            <div
              key={suggestion.category_id || idx}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 transition-all"
            >
              {/* Suggestion Header */}
              <div
                onClick={() => toggleSuggestion(suggestion.category_id)}
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${confidenceConfig.bg} flex items-center justify-center`}>
                      <ConfidenceIcon className={`w-5 h-5 ${confidenceConfig.text}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {suggestion.category_name || suggestion.category_id}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${confidenceConfig.bg} ${confidenceConfig.text}`}>
                          {suggestion.confidence} confidence
                        </span>
                        {suggestion.growth_rate !== undefined && (
                          <span className={`flex items-center gap-1 text-xs ${
                            suggestion.growth_rate >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {suggestion.growth_rate >= 0 ? (
                              <TrendingUp className="w-3 h-3" />
                            ) : (
                              <TrendingDown className="w-3 h-3" />
                            )}
                            {/* Handle both decimal (0.05) and percentage (5) formats */}
                            {suggestion.growth_rate >= 0 ? '+' : ''}
                            {Math.abs(suggestion.growth_rate) < 1
                              ? (suggestion.growth_rate * 100).toFixed(1)
                              : suggestion.growth_rate.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Budget Amount - Editable */}
                    <div className="text-right">
                      <input
                        type="number"
                        value={budgetValue}
                        onChange={(e) => handleBudgetEdit(suggestion.category_id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-32 text-right text-lg font-bold px-2 py-1 rounded border ${
                          isEdited
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                            : 'border-transparent bg-transparent text-green-600 dark:text-green-400'
                        } focus:outline-none focus:border-indigo-500`}
                      />
                      {isEdited && (
                        <p className="text-xs text-indigo-500 mt-1">Modified</p>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-0">
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-3 space-y-3">
                    {/* Reasoning */}
                    {suggestion.reasoning && (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">AI Reasoning</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                          {suggestion.reasoning}
                        </p>
                      </div>
                    )}

                    {/* Breakdown */}
                    {suggestion.breakdown && Object.keys(suggestion.breakdown).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Budget Breakdown</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {Object.entries(suggestion.breakdown).map(([name, amount], i) => (
                            <div
                              key={i}
                              className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-100 dark:border-gray-600"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate mr-2">
                                {name}
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                                {formatCurrency(amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Actions */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {Object.keys(editedBudgets).length > 0 && (
              <span className="text-indigo-600 dark:text-indigo-400">
                {Object.keys(editedBudgets).length} categories modified
              </span>
            )}
          </div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            Total: {formatCurrency(totalBudget)}
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={exportToCSV}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => onConfirm && onConfirm({
              action: 'submit',
              edited_budgets: editedBudgets,
              total_budget: totalBudget
            })}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-md"
          >
            Save Budget
          </button>
        </div>
      </div>
    </div>
    </div>
  );
};

export default BudgetSuggestionsDisplay;
